import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, ButtonProps, Modal, toGrammaticalNumber } from "@hex-insights/core";
import {
	SearchFormValues,
	SharingPolicyUsersPreviewQuery,
	useSearchFormState,
	useSharingPolicyUsersPreviewQuery,
} from "../../../../../../Utilities";
import { UsersListModal, UsersListModalProps } from "../../../../User";
import { SharingMenuFormValues } from "../../utils";

export type ControlledSharedUsersPreviewListModalControllerProps = Omit<ButtonProps, "onClick"> &
	Pick<ControlledSharedUsersPreviewListModalProps, "formValues">;

export function ControlledSharedUsersPreviewListModalController({
	formValues,
	...props
}: ControlledSharedUsersPreviewListModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button {...props} onClick={openModal} />

			<Modal.If condition={isModalOpen}>
				<ControlledSharedUsersPreviewListModal onClose={closeModal} formValues={formValues} />
			</Modal.If>
		</React.Fragment>
	);
}

export type ControlledSharedUsersPreviewListModalProps = Omit<
	UsersListModalProps<SharingPolicyUsersPreviewQuery["sharedUserConnection"]["edges"][0]["node"]>,
	"heading" | "isLoading" | "users" | "filterFormState" | "hasMore" | "loadMore"
> & {
	formValues: SharingMenuFormValues;
	pageSize?: number;
};

export function ControlledSharedUsersPreviewListModal({
	formValues,
	pageSize = 50,
	...props
}: ControlledSharedUsersPreviewListModalProps) {
	const searchFormState = useSearchFormState();
	const filterInput = IndexForms.useFilterInput(searchFormState.formValues, searchFormValuesToFilterInput);

	const { loading, data, fetchMore } = useSharingPolicyUsersPreviewQuery({
		variables: {
			sharingPolicy: formValues,
			filters: filterInput,
			pagination: { first: pageSize },
		},
	});
	const endCursor = data?.sharedUserConnection.pageInfo.endCursor;
	const loadMore = React.useCallback(() => {
		fetchMore({
			variables: { pagination: { first: pageSize, after: endCursor } },
			updateQuery: (prev, { fetchMoreResult }) => ({
				...prev,
				sharedUserConnection: {
					...prev.sharedUserConnection,
					pageInfo: fetchMoreResult.sharedUserConnection.pageInfo,
					edges: [...prev.sharedUserConnection.edges, ...fetchMoreResult.sharedUserConnection.edges],
				},
			}),
		});
	}, [fetchMore, pageSize, endCursor]);
	const users = React.useMemo(() => data?.sharedUserConnection.edges.map((e) => e.node), [data]);

	return (
		<UsersListModal
			isLoading={loading}
			users={users}
			filterFormState={searchFormState}
			hasMore={data?.sharedUserConnection.pageInfo.hasNextPage}
			loadMore={loadMore}
			heading={"Sharing with " + toGrammaticalNumber("Person", data?.sharedUserConnection.totalCount ?? 0, true)}
			{...props}
		/>
	);
}

function searchFormValuesToFilterInput(formValues: SearchFormValues) {
	return {
		search: formValues.search,
	};
}
